import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Home as HomeIcon,
  UtensilsCrossed,
  MapPin,
  MessageSquare,
  Camera,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api, resolveBackendAssetUrl } from "../lib/api";
import { CELIA_BOT_URL } from "../lib/celia";

const NAV = [
  { to: "/", label: "Inicio", icon: HomeIcon, testid: "nav-home" },
  { to: "/carta", label: "Carta y precios", icon: UtensilsCrossed, testid: "nav-carta" },
  { to: "/ubicacion", label: "Ubicación", icon: MapPin, testid: "nav-ubicacion" },
  { to: "/sugerencias", label: "Sugerencias", icon: MessageSquare, testid: "nav-sugerencias" },
  { to: "/galeria", label: "Sube tu foto", icon: Camera, testid: "nav-galeria" },
  { href: CELIA_BOT_URL, label: "Pregunta a Celia", icon: MessageSquare, testid: "nav-celia" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState({});
  const { pathname } = useLocation();
  const logoUrl = resolveBackendAssetUrl(media.logo, media._updated_at);

  useEffect(() => {
    api.get("/content/media", { params: { _t: Date.now() } })
      .then((r) => setMedia(r.data.data || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/60">
        <div className="mx-auto max-w-2xl lg:max-w-4xl flex items-center justify-between px-4 py-1.5">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            {logoUrl ? (
              <div className="h-12 w-12 sm:h-[52px] sm:w-[52px] rounded-full bg-white grid place-items-center p-1 shadow-sm border border-border/60">
                <img
                  src={logoUrl}
                  alt="Casa del Pueblo"
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <div className="h-9 w-9 rounded-full bg-primary grid place-items-center text-primary-foreground font-black text-lg shadow-sm">
                C
              </div>
            )}
            <div className="leading-tight">
              <div className="font-display font-black text-lg tracking-tight">Casa del Pueblo</div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Feria de Albacete</div>
            </div>
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="h-10 w-10 grid place-items-center rounded-full hover:bg-accent transition"
            data-testid="menu-toggle"
            aria-label="Menú"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-border/60 bg-background" data-testid="mobile-nav">
            <div className="mx-auto max-w-2xl lg:max-w-4xl grid grid-cols-2 sm:grid-cols-3 gap-1 p-3">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = n.to && pathname === n.to;
                const className = `flex items-center gap-2 px-3 py-2.5 rounded-xl transition ${
                  active ? "bg-primary text-primary-foreground" : "hover:bg-accent"
                }`;
                if (n.href) {
                  return (
                    <a
                      key={n.href}
                      href={n.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpen(false)}
                      data-testid={n.testid}
                      className={className}
                    >
                      <Icon size={18} />
                      <span className="text-sm font-medium">{n.label}</span>
                    </a>
                  );
                }
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={() => setOpen(false)}
                    data-testid={n.testid}
                    className={className}
                  >
                    <Icon size={18} />
                    <span className="text-sm font-medium">{n.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-2xl lg:max-w-4xl px-4 pb-24 pt-4">
        <Outlet />
      </main>

      <footer className="border-t border-border/60 bg-accent/40">
        <div className="mx-auto max-w-2xl lg:max-w-4xl px-4 py-8 text-sm text-muted-foreground space-y-2">
          <div className="font-display font-bold text-foreground text-base">Casa del Pueblo</div>
          <div>Recinto Ferial de Albacete · junto al R2 · 9 arcos</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2">
            <Link to="/sugerencias" className="hover:text-primary" data-testid="footer-sugerencias">Sugerencias</Link>
            <Link to="/admin/login" className="hover:text-primary" data-testid="footer-admin">Acceso staff</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
